/**
 * docLinkConditionColors — recolor CML/TML markup-link markers in the document
 * viewer by their target CML's CONDITION RATING (AVA-348), so doc links match the
 * 3D/360 markers instead of showing the flat link-type color.
 *
 * Why render-then-patch: a markup-link carries its cmlId at render time, but the
 * condition RATING is NOT loaded in the plain doc-viewer context (no monitoring
 * cache). So markers draw immediately with the link-type fallback color, then this
 * module fetches the facility's coloring map once and patches the shapes in.
 *
 * Guards (from the Codex plan review):
 *  - assetId-keyed cache — never a global unstamped cache
 *    (see project_projects_cache_global_unstamped).
 *  - request token + facility re-check before painting, so a page-flip or a
 *    facility-switch mid-fetch cannot paint stale / wrong-facility markers.
 *  - LIVE-DOM query at paint time (not cached element refs) — self-heals across the
 *    per-page label .remove()/recreate cycle.
 *  - resolveLinkCmlId wraps JSON.parse in try/catch — one malformed link.metadata
 *    must never throw and drop the whole render loop.
 *  - fail-open: any fetch/permission error keeps the existing link-type colors.
 *
 * See .claude/features/CML_TML_MARKER_SHAPE_UNIFICATION.md §4C / §9.
 */
import store from '@/store';
import { getCmlColoring } from '@/features/Tags/components/monitoring/cmlColoringService';
import { log as debugLog } from '@/utils/debugLogger';
import { lightenHex } from '@/utils/colorUtils';

/**
 * Resolve a link's target CML id from the top-level field or the metadata JSON.
 * ForgeMarkupLink.clone() drops the top-level cmlId, so every consumer must fall
 * back to metadata.cmlId (mirrors useCrossViewerSync / linkPreviewPopup). TML links
 * carry cmlId in metadata too (color inherits from the parent CML — D14).
 * @param {object} link
 * @returns {string|null}
 */
export function resolveLinkCmlId(link) {
  if (!link) return null;
  if (link.cmlId) return link.cmlId;
  const meta = link.metadata;
  if (meta && typeof meta === 'object') return meta.cmlId || null;
  if (typeof meta === 'string' && meta) {
    try {
      return JSON.parse(meta)?.cmlId || null;
    } catch (err) {
      debugLog('[docLinkColor] bad link.metadata JSON — skipping cmlId', err);
      return null;
    }
  }
  return null;
}

// assetId → Map(cmlId → conditionRating enum-name | null). Session-lifetime per
// facility; ratings shift only after new readings (same staleness the 3D coloring
// accepts). Clear on demand via clearDocLinkColorCache().
const _ratingByFacility = new Map();
// Facilities whose coloring fetch returned 403 (a doc-only user lacks Masters/
// InspectionTags permission) — remembered so we don't refetch and re-403 on every
// page navigation this session.
const _deniedFacilities = new Set();
// Latest paint request id PER container (not global): two viewers recoloring
// concurrently (e.g. the main PDF viewer and the Dashboard's embedded doc viewer)
// must not cancel each other — only a newer paint for the SAME container (e.g. a
// page flip) supersedes. WeakMap keyed by the container element → no leak, no cleanup.
const _reqSeqByContainer = new WeakMap();

async function loadRatingMap(assetId) {
  if (_ratingByFacility.has(assetId)) return _ratingByFacility.get(assetId);
  // No tagTypes / no masterTagId → every CML in the facility (verified against the
  // API: CmlColoringService applies the type filter only when tagTypes is non-empty).
  const rows = await getCmlColoring({ includeArchived: true });
  const map = new Map();
  (rows || []).forEach((r) => {
    if (r && r.id) map.set(r.id, r.conditionRating);
  });
  _ratingByFacility.set(assetId, map);
  return map;
}

/**
 * Drop the cached coloring map (e.g. after readings change and colors must refresh).
 * @param {string} [assetId] - clear one facility; omit to clear all.
 */
export function clearDocLinkColorCache(assetId) {
  if (assetId) {
    _ratingByFacility.delete(assetId);
    _deniedFacilities.delete(assetId);
  } else {
    _ratingByFacility.clear();
    _deniedFacilities.clear();
  }
}

/**
 * Patch condition colors onto every CML/TML doc-link marker under `container`.
 * Safe to call after each render pass; unknown CMLs and any error keep the
 * link-type fallback color (no regression).
 * @param {HTMLElement} container - the viewer container holding .markup-link labels
 */
export async function applyCmlConditionColorsToDocLinks(container) {
  if (!container) return;
  // Nothing to recolor → skip the facility-wide fetch entirely. Most pages have no
  // CML/TML links, and this also avoids a per-navigation 403 for doc-only users.
  if (!container.querySelector('.markup-link[data-cml-id]')) return;

  const forgeService = store.state.forgeService;
  const assetId = store.state.assetId;
  if (!forgeService || !assetId) return;
  if (_deniedFacilities.has(assetId)) return; // permission denied earlier this session

  // Per-container token: a newer paint for THIS container supersedes this one; a
  // concurrent paint for a DIFFERENT container does NOT (that was the "silently skip
  // a viewer" bug a global counter caused).
  const reqId = (_reqSeqByContainer.get(container) || 0) + 1;
  _reqSeqByContainer.set(container, reqId);

  let ratingMap;
  try {
    ratingMap = await loadRatingMap(assetId);
  } catch (err) {
    // Fail-open: keep the link-type colors. A 403 (doc-only user lacks Masters/
    // InspectionTags permission) is stable for the session — remember it so we don't
    // refetch and re-403 on every page navigation. Transient errors are not cached.
    if (err && err.response && err.response.status === 403) _deniedFacilities.add(assetId);
    debugLog('[docLinkColor] getCmlColoring failed — keeping link-type colors', err);
    return;
  }

  // Supersede guard: a newer paint for THIS container, or a facility switch, wins.
  if (_reqSeqByContainer.get(container) !== reqId || store.state.assetId !== assetId) return;

  // LIVE query — the per-page .remove()/recreate cycle means cached refs would be stale.
  const nodes = container.querySelectorAll('.markup-link[data-cml-id]');
  let patched = 0;
  nodes.forEach((label) => {
    const cmlId = label.getAttribute('data-cml-id');
    if (!cmlId || !ratingMap.has(cmlId)) return; // unknown CML → keep fallback color
    const color = forgeService._getCmlConditionColor({ conditionRating: ratingMap.get(cmlId) });
    if (!color) return;
    const shape = label.querySelector('.markup-link-dot');
    if (shape) shape.style.backgroundColor = color;
    const tooltip = label.querySelector('.markup-link-tooltip');
    if (tooltip) {
      tooltip.style.borderColor = color;
      tooltip.style.background = lightenHex(color, 0.7);
    }
    patched += 1;
  });
  debugLog(`[docLinkColor] patched ${patched}/${nodes.length} CML/TML doc-link markers (facility ${assetId})`);
}
